<span cdkOverlayOrigin #origin="cdkOverlayOrigin" class="zag-date-picker__origin">
  @if (triggerMode() === 'button') {
  <button
    type="button"
    class="zag-date-picker__trigger-btn"
    [class.zag-date-picker__trigger-btn--open]="open()"
    [class.zag-date-picker__trigger-btn--field]="triggerField()"
    [disabled]="disabled()"
    [attr.aria-expanded]="open()"
    [attr.aria-haspopup]="'dialog'"
    (click)="openPanel()"
  >
    @if (triggerLeadingIcon()) {
    <span [zagIcon]="triggerLeadingIcon()" class="zag-date-picker__trigger-btn-icon"></span>
    }
    <span class="zag-date-picker__trigger-btn-label">{{ buttonLabel() }}</span>
    <span zagIcon="expand_more" class="zag-date-picker__trigger-btn-chevron"></span>
  </button>
  } @else if (triggerMode() === 'input') {
  <div class="zag-date-picker__input-wrapper" [class.zag-date-picker__input-wrapper--error]="inputError()">
    <input
      [zagScrollIntoView]="true"
      type="text"
      class="zag-date-picker__input"
      [attr.id]="id()"
      [attr.name]="name()"
      [attr.placeholder]="maskInput() ? maskPlaceholder() : (placeholder() ?? ('picker.calendar' | translate))"
      [attr.aria-label]="placeholder() ?? ('picker.calendar' | translate)"
      [attr.aria-invalid]="inputError()"
      [attr.aria-required]="required()"
      [attr.aria-expanded]="open()"
      [attr.aria-haspopup]="'dialog'"
      [disabled]="disabled()"
      [readonly]="readonly() || isMobile()"
      [value]="inputText()"
      (input)="onInputChange($event)"
      (blur)="onInputBlur($event)"
      (focus)="onInputFocus()"
      (keydown)="onInputKeyDown($event)"
    />

    @if (showClearButton()) {
    <button type="button" class="zag-date-picker__clear" [attr.aria-label]="'picker.clearDate' | translate" (click)="clear($event)">
      <span zagIcon="close"></span>
    </button>
    }
  </div>
  }
</span>

@if (isInline()) {
<ng-container *ngTemplateOutlet="panelTpl; context: { inline: true }"></ng-container>
} @else {
<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOrigin]="origin"
  [cdkConnectedOverlayOpen]="open()"
  [cdkConnectedOverlayHasBackdrop]="true"
  [cdkConnectedOverlayBackdropClass]="isMobile() ? 'zag-picker-dialog-backdrop' : 'zag-date-picker-overlay-backdrop'"
  [cdkConnectedOverlayPanelClass]="isMobile() ? 'zag-picker-dialog-panel' : ''"
  [cdkConnectedOverlayPositions]="isMobile() ? mobileOverlayPositions : overlayPositions"
  (backdropClick)="isMobile() ? closeMobile() : close()"
  (detach)="isMobile() ? closeMobile() : close()"
>
  <ng-container *ngTemplateOutlet="panelTpl; context: { inline: false }"></ng-container>
</ng-template>
}

<ng-template #panelTpl let-inline="inline">
  <div
    class="zag-date-picker-panel"
    [class.zag-date-picker-panel--inline]="inline"
    [attr.role]="inline ? 'group' : 'dialog'"
    [attr.aria-modal]="inline ? null : 'true'"
    [attr.aria-label]="'picker.calendar' | translate"
  >
    <div class="zag-date-picker-panel__header zag-date-picker-panel__header--toolbar">
      <div class="zag-date-picker-panel__nav-group">
        <button
          type="button"
          class="zag-date-picker-panel__nav"
          [attr.aria-label]="'picker.previous' | translate"
          (click)="navigatePrevious()"
        >
          <span zagIcon="expand_less"></span>
        </button>
        <button type="button" class="zag-date-picker-panel__nav" [attr.aria-label]="'picker.next' | translate" (click)="navigateNext()">
          <span zagIcon="expand_more"></span>
        </button>
      </div>

      <button type="button" class="zag-date-picker-panel__title" [disabled]="isZoomOutDisabled()" (click)="zoomOut()">
        @if (!inline && fieldLabel()) {
        <span class="zag-date-picker-panel__title-context">{{ fieldLabel() }}</span>
        } {{ headerLabel() }}
      </button>

      @if (!isTodaySelected()) {
      <button
        type="button"
        class="zag-date-picker-panel__nav zag-date-picker-panel__nav--today"
        [attr.aria-label]="'calendars.toolbar.today' | translate"
        [zagTooltip]="'calendars.toolbar.today' | translate"
        (click)="goToday()"
      >
        <span zagIcon="calendar-today"></span>
      </button>
      } @if (!inline) {
      <button
        type="button"
        class="zag-date-picker-panel__nav zag-date-picker-panel__nav--close"
        [attr.aria-label]="'picker.close' | translate"
        [zagTooltip]="'picker.close' | translate"
        (click)="closeMobile()"
      >
        <span zagIcon="close"></span>
      </button>
      }
    </div>
    @if (viewMode() === 'day' && selectionMode() === 'week') {
    <!-- Week-row selection mode -->
    <div class="zag-date-picker-panel__weekdays zag-date-picker-panel__weekdays--with-week-number">
      <span class="zag-date-picker-panel__week-number-header">{{ 'calendar.week' | translate }}</span>
      @for (weekday of i18nWeekdayLabels(); track weekday) {
      <span class="zag-date-picker-panel__weekday">{{ weekday }}</span>
      }
    </div>
    <div class="zag-date-picker-panel__week-rows">
      @for (row of weekRows(); track row.weekStartIso) {
      <button
        type="button"
        class="zag-date-picker-panel__week-row"
        [class.zag-date-picker-panel__week-row--selected]="row.isSelected"
        [class.zag-date-picker-panel__week-row--current]="row.isCurrent"
        [class.zag-date-picker-panel__week-row--range-start]="row.isRangeStart"
        [class.zag-date-picker-panel__week-row--in-range]="row.isInRange"
        [class.zag-date-picker-panel__week-row--range-end]="row.isRangeEnd"
        [attr.data-week-iso]="row.weekStartIso"
        [attr.aria-selected]="row.isSelected || row.isInRange"
        (click)="selectWeekRow(row, $event)"
        (keydown)="onWeekRowKeyDown($event, row)"
      >
        <span class="zag-date-picker-panel__week-number">{{ row.weekNumber }}</span>
        @for (day of row.days; track day.date.toISOString()) {
        <span
          class="zag-date-picker-panel__week-day"
          [class.zag-date-picker-panel__week-day--other-month]="!day.isCurrentMonth"
          [class.zag-date-picker-panel__week-day--today]="day.isToday"
          >{{ day.dayOfMonth }}</span
        >
        }
      </button>
      }
    </div>
    } @else if (viewMode() === 'day') { @if (showDayWeekNumbers()) {
    <div class="zag-date-picker-panel__weekdays zag-date-picker-panel__weekdays--with-week-number">
      <span class="zag-date-picker-panel__week-number-header">{{ 'calendar.week' | translate }}</span>
      @for (weekday of weekdayLabels(); track weekday) {
      <span class="zag-date-picker-panel__weekday">{{ weekday }}</span>
      }
    </div>

    <div class="zag-date-picker-panel__grid zag-date-picker-panel__grid--days zag-date-picker-panel__grid--days-with-week-number">
      @for (week of dayWeeks(); track week.weekStartIso) {
      <span class="zag-date-picker-panel__week-number">{{ week.weekNumber }}</span>
      @for (cell of week.cells; track cell.iso) {
      <ng-container *ngTemplateOutlet="dayCellTpl; context: { $implicit: cell }"></ng-container>
      } }
    </div>
    } @else {
    <div class="zag-date-picker-panel__weekdays">
      @for (weekday of weekdayLabels(); track weekday) {
      <span class="zag-date-picker-panel__weekday">{{ weekday }}</span>
      }
    </div>

    <div class="zag-date-picker-panel__grid zag-date-picker-panel__grid--days">
      @for (cell of dayCells(); track cell.iso) {
      <ng-container *ngTemplateOutlet="dayCellTpl; context: { $implicit: cell }"></ng-container>
      }
    </div>
    } } @if (viewMode() === 'month') {
    <div class="zag-date-picker-panel__grid zag-date-picker-panel__grid--months">
      @for (cell of monthCells(); track cell.iso) {
      <button
        type="button"
        class="zag-date-picker-panel__cell zag-date-picker-panel__cell--option"
        [class.zag-date-picker-panel__cell--selected]="cell.isSelected"
        [class.zag-date-picker-panel__cell--today]="cell.isCurrent"
        [class.zag-date-picker-panel__cell--range-start]="cell.isRangeStart"
        [class.zag-date-picker-panel__cell--in-range]="cell.isInRange"
        [class.zag-date-picker-panel__cell--range-end]="cell.isRangeEnd"
        [disabled]="cell.isDisabled"
        [attr.data-option-iso]="cell.iso"
        [attr.aria-selected]="cell.isSelected || cell.isInRange"
        (click)="selectMonth(cell, $event)"
        (keydown)="onMonthCellKeyDown($event, cell)"
      >
        {{ cell.label }}
      </button>
      }
    </div>
    } @if (viewMode() === 'quarter') {
    <div class="zag-date-picker-panel__grid zag-date-picker-panel__grid--quarters">
      @for (cell of quarterCells(); track cell.iso) {
      <button
        type="button"
        class="zag-date-picker-panel__cell zag-date-picker-panel__cell--option zag-date-picker-panel__cell--quarter"
        [class.zag-date-picker-panel__cell--selected]="cell.isSelected"
        [class.zag-date-picker-panel__cell--today]="cell.isCurrent"
        [class.zag-date-picker-panel__cell--range-start]="cell.isRangeStart"
        [class.zag-date-picker-panel__cell--in-range]="cell.isInRange"
        [class.zag-date-picker-panel__cell--range-end]="cell.isRangeEnd"
        [disabled]="cell.isDisabled"
        [attr.data-option-iso]="cell.iso"
        [attr.aria-selected]="cell.isSelected || cell.isInRange"
        (click)="selectQuarter(cell, $event)"
        (keydown)="onQuarterCellKeyDown($event, cell)"
      >
        {{ cell.label }}
      </button>
      }
    </div>
    } @if (viewMode() === 'year') {
    <div class="zag-date-picker-panel__grid zag-date-picker-panel__grid--years">
      @for (cell of yearCells(); track cell.iso) {
      <button
        type="button"
        class="zag-date-picker-panel__cell zag-date-picker-panel__cell--option"
        [class.zag-date-picker-panel__cell--outside]="cell.isOutsideRange"
        [class.zag-date-picker-panel__cell--selected]="cell.isSelected"
        [class.zag-date-picker-panel__cell--today]="cell.isCurrent"
        [class.zag-date-picker-panel__cell--range-start]="cell.isRangeStart"
        [class.zag-date-picker-panel__cell--in-range]="cell.isInRange"
        [class.zag-date-picker-panel__cell--range-end]="cell.isRangeEnd"
        [disabled]="cell.isDisabled"
        [attr.data-option-iso]="cell.iso"
        [attr.aria-selected]="cell.isSelected || cell.isInRange"
        (click)="selectYear(cell, $event)"
        (keydown)="onYearCellKeyDown($event, cell)"
      >
        {{ cell.label }}
      </button>
      }
    </div>
    } @if (viewMode() === 'decade') {
    <div class="zag-date-picker-panel__grid zag-date-picker-panel__grid--decades">
      @for (cell of decadeCells(); track cell.value) {
      <button
        type="button"
        class="zag-date-picker-panel__cell zag-date-picker-panel__cell--option"
        [class.zag-date-picker-panel__cell--outside]="cell.isOutsideRange"
        [class.zag-date-picker-panel__cell--selected]="cell.isSelected"
        [class.zag-date-picker-panel__cell--today]="cell.isCurrent"
        [disabled]="cell.isDisabled"
        (click)="selectDecade(cell)"
      >
        {{ cell.label }}
      </button>
      }
    </div>
    } @if (isMobile() && !inline) {
    <div class="zag-picker-mobile-footer">
      <button
        type="button"
        class="zag-picker-mobile-footer__apply"
        [disabled]="rangeSelection() ? !pendingRange() : !pendingDate()"
        (click)="applyMobile()"
      >
        {{ 'picker.apply' | translate }}
      </button>
    </div>
    }
    <ng-template #dayCellTpl let-cell>
      <button
        type="button"
        class="zag-date-picker-panel__cell"
        [class.zag-date-picker-panel__cell--muted]="!cell.isCurrentMonth"
        [class.zag-date-picker-panel__cell--today]="cell.isToday"
        [class.zag-date-picker-panel__cell--selected]="cell.isSelected"
        [class.zag-date-picker-panel__cell--range-start]="cell.isRangeStart"
        [class.zag-date-picker-panel__cell--in-range]="cell.isInRange"
        [class.zag-date-picker-panel__cell--range-end]="cell.isRangeEnd"
        [disabled]="cell.isDisabled"
        [attr.data-day-iso]="cell.iso"
        [attr.aria-label]="cell.label"
        [attr.aria-selected]="cell.isSelected || cell.isInRange"
        (click)="selectDay(cell, $event)"
        (keydown)="onDayCellKeyDown($event, cell)"
      >
        {{ cell.dayOfMonth }}
      </button>
    </ng-template>
  </div>
</ng-template>
